import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useParams } from 'react-router-dom'
import { ifcRootGuid } from '@constants'
import produce from 'immer'
import { findIndex } from 'lodash-es'
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab'
import { Alert, Box, Button, Stack, Tab, Typography, styled } from '@mui/material'
import type { GlbFileAdditionalData } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CorneredSection } from '@ui2/structure'
import { SwitchLabeled } from '@ui/actions'
import { GenericErrorBoundaryFallback } from '@ui/errors'
import { Form2 } from '@ui/forms'
import { useControlStore } from '@editorStores'
import { useIfcFiles, useCameraEvent } from '@editorHooks'
import { getProjectFiles } from '@queries'
import { postFileMetadata } from '@mutations'
import AutoAlignmentTab from './components/AutoAlignmentTab'
import CameraOriginSelect from './components/CameraOriginSelect'
import CopyAlignmentTab from './components/CopyAlignmentTab'
import FormFields from './components/FormFields'
import { AlignmentFormValues } from './types'
import { alignmentConfigToDefaultValues, formValuesToAlignmentConfig } from './utils'

/**
 * Sub-tabs of the "Modell ausrichten" section: automatic bounding-box alignment, scene
 * point-picking, manual text inputs, or copying the alignment from another IFC file.
 */
type ModelAlignmentTab = 'automatic' | 'fromModel' | 'advanced' | 'copyFrom'

const StyledTabPanel = styled(TabPanel)({ padding: 0 })

interface Props {
  /**
   * Extra content rendered in the alignment section between the camera-view select and the
   * "Ausrichtung anwenden" switch. Used to host the scene point-picking actions of the IFC importer.
   */
  alignmentActions?: ReactNode
  /**
   * Disables the manual alignment controls (apply switch, reference-point form) — e.g. while an
   * alignment is being defined in the scene. The camera-view select and `alignmentActions` stay
   * enabled.
   */
  alignmentFieldsDisabled?: boolean
}

/**
 * Alignment editor for the active IFC model: a "Kamerasicht wechseln" camera-origin select and a
 * "Modell ausrichten" section (scene point-picking via `alignmentActions`, or the manual
 * reference-point/z-rotation form). Saves the alignment config on the file metadata.
 */
const IFCAlignmentSection = ({
  alignmentActions,
  alignmentFieldsDisabled = false,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const applyIfcAlignmentConfig = useControlStore(state => state.applyIfcAlignmentConfig)
  const setApplyIfcAlignmentConfig = useControlStore(state => state.setApplyIfcAlignmentConfig)
  const storey = useControlStore(state => state.floorplanState.storey)

  const glbFiles = useIfcFiles()
  const activeFile = useMemo(
    () =>
      activeIfcFileGuid
        ? glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid) ?? null
        : null,
    [activeIfcFileGuid, glbFiles],
  )
  const alignmentConfig = useMemo(
    () => (activeFile?.additional_data as GlbFileAdditionalData | undefined)?.alignment_config,
    [activeFile?.additional_data],
  )

  // Other IFC files whose alignment config can be copied onto the active file.
  const copySourceFiles = useMemo(
    () =>
      glbFiles.filter(
        (file: FileItem) =>
          file.guid !== activeIfcFileGuid &&
          !!(file.additional_data as GlbFileAdditionalData | undefined)?.alignment_config,
      ),
    [glbFiles, activeIfcFileGuid],
  )

  const [cameraTargetFromClusters, setCameraTargetFromClusters] = useState<
    [number, number, number] | null
  >(null)

  const camPosition = useMemo(() => {
    if (storey !== null) return null
    if (!cameraTargetFromClusters) return null
    return {
      cameraType: 'perspective' as const,
      type: 'positionPerspective' as const,
      position: cameraTargetFromClusters,
      animateIn: false,
      animateOut: false,
      priority: 2,
    }
  }, [storey, cameraTargetFromClusters])

  useCameraEvent(camPosition)

  const queryClient = useQueryClient()
  const { mutate: saveAlignmentConfig, isLoading: isSavingAlignmentConfig } = useMutation({
    mutationFn: async (payload: {
      alignment_config: GlbFileAdditionalData['alignment_config']
    }) => {
      if (!projectId || !activeFile) return null
      return postFileMetadata.request(projectId, ifcRootGuid, {
        item_guid: activeFile.guid,
        name: activeFile.name ?? '',
        ...(activeFile.additional_data || {}),
        alignment_config: payload.alignment_config,
      })
    },
    onSuccess: (data: FileItem | null) => {
      if (!data) return
      const queryKey = getProjectFiles.getKey(projectId!, ifcRootGuid)
      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return
          const index = findIndex(draft.files, { guid: data.guid })
          if (index !== -1) draft.files[index].additional_data = data.additional_data
        }),
      )
    },
    mutationKey: ['saveAlignmentConfig', projectId, ifcRootGuid, activeIfcFileGuid],
    meta: {
      title: 'Anpassen der IFC-Ausrichtung',
      description: 'Die Ausrichtung des IFC-Modells in der Scene',
    },
  })

  const defaultValues = useMemo(
    () => alignmentConfigToDefaultValues(alignmentConfig),
    [alignmentConfig],
  )

  const handleSubmit = useCallback(
    (values: AlignmentFormValues) => {
      saveAlignmentConfig({ alignment_config: formValuesToAlignmentConfig(values) })
    },
    [saveAlignmentConfig],
  )

  const handleReset = useCallback(() => {
    saveAlignmentConfig({
      alignment_config: {
        referencePointIFCCS: [0, 0, 0],
        referencePointModelCS: [0, 0, 0],
        zRotationDegrees: 0,
      },
    })
  }, [saveAlignmentConfig])

  const [modelAlignmentTab, setModelAlignmentTab] = useState<ModelAlignmentTab>(
    alignmentActions ? 'automatic' : 'advanced',
  )
  // The copy tab can lose its source files (e.g. when the active file changes) while selected —
  // fall back to the first tab instead of showing a disabled selected tab.
  const effectiveAlignmentTab =
    modelAlignmentTab === 'copyFrom' && copySourceFiles.length === 0
      ? 'automatic'
      : modelAlignmentTab

  if (!activeIfcFileGuid) {
    return (
      <Alert severity="info">
        Wählen Sie ein IFC-Modell aus, um die Ausrichtung zu konfigurieren.
      </Alert>
    )
  }

  // Manual alignment via the text fields — disabled while an alignment is being defined in the scene.
  const advancedAlignmentFields = (
    <Box
      component="fieldset"
      disabled={alignmentFieldsDisabled}
      sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
    >
      <Stack spacing={1}>
        <Typography>
          Ausrichtung: P<sub>IFC,neu</sub> = Rotate[P<sub>IFC</sub> - P<sub>IFC,ref</sub>] + P
          <sub>Modell,ref</sub>
        </Typography>

        <SwitchLabeled
          checked={applyIfcAlignmentConfig}
          onChange={event => setApplyIfcAlignmentConfig(event.target.checked)}
          label="Ausrichtung anwenden"
          data-cy="switch-apply-ifc-alignment"
        />

        <Form2<AlignmentFormValues>
          key={`alignment-form-${activeFile?.guid ?? ''}-${JSON.stringify(defaultValues)}`}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
        >
          <Stack direction="column" spacing={1}>
            <FormFields applyIfcAlignmentConfig={applyIfcAlignmentConfig} />
            <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
              <LoadingButton
                type="submit"
                variant="contained"
                size="small"
                loading={isSavingAlignmentConfig}
              >
                Speichern
              </LoadingButton>
              <Button
                variant="outlined"
                size="small"
                onClick={handleReset}
                disabled={isSavingAlignmentConfig}
              >
                Zurücksetzen
              </Button>
            </Stack>
          </Stack>
        </Form2>
      </Stack>
    </Box>
  )

  return (
    <Stack direction="column" spacing={1.5}>
      <CorneredSection title="Kamerasicht wechseln" collapsible defaultOpen>
        <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
          <CameraOriginSelect onCameraTargetChange={setCameraTargetFromClusters} />
        </ErrorBoundary>
      </CorneredSection>

      <CorneredSection title="Modell ausrichten" collapsible defaultOpen>
        {alignmentActions ? (
          <TabContext value={effectiveAlignmentTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={(_event, value: ModelAlignmentTab) => setModelAlignmentTab(value)}
                sx={{
                  minHeight: 32,
                  '& .MuiTab-root': { minHeight: 32, textTransform: 'none', py: 0.5 },
                }}
              >
                <Tab label="Automatisch" value="automatic" data-cy="tab-alignment-automatic" />
                <Tab label="Aus Modell" value="fromModel" data-cy="tab-alignment-from-model" />
                <Tab
                  label="Kopieren von"
                  value="copyFrom"
                  disabled={copySourceFiles.length === 0}
                  data-cy="tab-alignment-copy-from"
                />
                <Tab label="Erweitert" value="advanced" data-cy="tab-alignment-advanced" />
              </TabList>
            </Box>

            <StyledTabPanel value="automatic">
              <Box sx={{ pt: 1 }}>
                <AutoAlignmentTab
                  onAlign={config => saveAlignmentConfig({ alignment_config: config })}
                  onReset={handleReset}
                />
              </Box>
            </StyledTabPanel>

            <StyledTabPanel value="fromModel">
              <Box sx={{ pt: 1 }}>{alignmentActions}</Box>
            </StyledTabPanel>

            <StyledTabPanel value="advanced">
              <Box sx={{ pt: 1 }}>{advancedAlignmentFields}</Box>
            </StyledTabPanel>

            <StyledTabPanel value="copyFrom">
              <Box sx={{ pt: 1 }}>
                <CopyAlignmentTab
                  sourceFiles={copySourceFiles}
                  onCopy={config => saveAlignmentConfig({ alignment_config: config })}
                  onReset={handleReset}
                />
              </Box>
            </StyledTabPanel>
          </TabContext>
        ) : (
          advancedAlignmentFields
        )}
      </CorneredSection>
    </Stack>
  )
}

export default IFCAlignmentSection
